import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../app/AuthContext";
import type { TreeEdge, TreeGraph, TreeNode, TreeOccurrence, TreeRun } from "../../shared/api/documentTreeTypes";
import { collectPermissions } from "../../shared/lib/permissions";
import { Button, Callout, Field, Input, Spinner } from "../../shared/ui/Primitives";
import { indexTreeOccurrences } from "./document-tree-view";

const statuses: Record<TreeNode["search_status"], string> = {
  found: "Найден", not_found: "Не найден в доступных источниках", ambiguous: "Неоднозначно",
  unprocessed: "Не обработан", error: "Ошибка", not_required: "Отдельный файл не требуется",
  not_checked: "Не проверено",
};
const kinds: Record<string, string> = { product: "Изделие", assembly: "Сборочная единица", detail: "Деталь",
  document: "Документ", standard_item: "Стандартное изделие", material: "Материал", purchased_item: "Покупное изделие", other: "Другой объект" };
const warningNames: Record<string, string> = {
  depth_limit: "Достигнут предел глубины", node_limit: "Достигнут предел объектов", edge_limit: "Достигнут предел связей",
  occurrence_limit: "Достигнут предел отображаемых вхождений", time_limit: "Истекло время построения",
  cycle_detected: "Обнаружены циклические ссылки", specialist_review_required: "Есть элементы для проверки специалистом",
  conflicting_sources: "Есть конфликтующие сведения источников", conflicting_names: "Источники называют объект по-разному",
  multiple_candidates: "Несколько файлов-кандидатов", uncertain_relation: "Сомнительная исходная связь",
  source_analysis_partial: "Анализ источника содержит предупреждения", uncertain_document_type: "Тип PDF не установлен уверенно",
  candidate_limit: "Список кандидатов ограничен",
};
const reasons: Record<string, string> = { reference_or_partial_match_only: "Только упоминание или неполное совпадение",
  conflicting_primary_metadata: "Основные метаданные противоречат имени файла",
  incompatible_document_type: "Неподходящий тип документа" };
const relations: Record<string, string> = { mentions: "Упоминание", specification_item: "Позиция спецификации",
  includes_document: "Включён в перечень", requires_drawing: "Чертёж по правилу" };
const message = (error: unknown) => error instanceof Error ? error.message : "Не удалось получить дерево";

export function DocumentTreePanel() {
  const { api, user } = useAuth();
  const permissions = useMemo(() => collectPermissions(user), [user]);
  const [designation, setDesignation] = useState("");
  const [run, setRun] = useState<TreeRun | null>(null);
  const [history, setHistory] = useState<TreeRun[]>([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [graph, setGraph] = useState<TreeGraph | null>(null);
  const [selected, setSelected] = useState<{ node: TreeNode; edge?: TreeEdge } | null>(null);
  const [pdf, setPdf] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const active = run?.status === "queued" || run?.status === "running";
  const nodes = useMemo(() => new Map(graph?.nodes.map(n => [n.id, n])), [graph]);
  const edges = useMemo(() => new Map(graph?.edges.map(e => [e.id, e])), [graph]);
  const children = useMemo(() => indexTreeOccurrences(graph?.occurrences ?? []), [graph]);

  useEffect(() => {
    if (!run?.id || !active) return;
    let alive = true;
    const timer = setInterval(() => { void api.getDocumentTree(run.id).then(value => { if (alive) setRun(value); })
      .catch(error => { if (alive) setError(message(error)); }); }, 3000);
    return () => { alive = false; clearInterval(timer); };
  }, [api, run?.id, active]);
  useEffect(() => {
    setGraph(null); setSelected(null);
    if (!run?.id || active) return;
    let alive = true;
    void api.getDocumentTreeGraph(run.id).then(value => { if (alive) setGraph(value); })
      .catch(error => { if (alive) setError(message(error)); });
    return () => { alive = false; };
  }, [api, run?.id, active]);
  useEffect(() => {
    setPdf("");
    const id = selected?.edge?.evidence.analysis_id;
    if (!id) return;
    let alive = true; let url = "";
    void api.downloadDocumentAnalysisPdf(id).then(file => {
      if (alive) { url = URL.createObjectURL(file.blob); setPdf(url); }
    }).catch(error => { if (alive) setError(message(error)); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [api, selected?.edge?.evidence.analysis_id]);

  async function act(action: () => Promise<void>) {
    setError(""); setBusy(true);
    try { await action(); } catch (error) { setError(message(error)); } finally { setBusy(false); }
  }
  async function loadHistory(offset: number) {
    const value = await api.documentTreeHistory(designation, offset);
    setHistory(value.items); setHistoryOffset(offset); setHistoryTotal(value.total);
  }
  function renderOccurrence(item: TreeOccurrence) {
    const node = nodes.get(item.node_id);
    if (!node) return null;
    const edge = item.edge_id ? edges.get(item.edge_id) : undefined;
    const nested = children.get(item.id) ?? [];
    return <li key={item.id}>
      <details open={item.depth === 0}>
        <summary>{node.designation_original ?? node.name ?? "Без обозначения"} · {kinds[node.object_type]} · {statuses[node.search_status]}
          {node.name && node.name !== node.designation_original ? ` · ${node.name}` : ""}
          {item.flags.map(flag => ` · ${flag === "cycle" ? "Цикл — обход остановлен" : warningNames[flag] ?? "Ветка не раскрыта"}`)}</summary>
        <Button size="small" variant="ghost" onClick={() => setSelected({ node, edge })}>Кандидаты и основание связи</Button>
        {nested.length ? <ul style={{ paddingLeft: "1.2rem" }}>{nested.map(renderOccurrence)}</ul> : null}
      </details>
    </li>;
  }
  if (!permissions.has("documents.view")) return null;
  return <details className="kd-upload"><summary>Дерево конструкторской документации</summary>
    <Callout>Дерево строится из уже проиндексированных и проанализированных PDF. Отсутствие файла в доступном каталоге не доказывает отсутствие документа. Актуальность редакций не утверждается.</Callout>
    <form className="stack" onSubmit={event => { event.preventDefault(); void act(async () => {
      setRun(await api.buildDocumentTree(designation));
    }); }}>
      <Field label="Корневое обозначение"><Input value={designation} maxLength={250} placeholder="Стал.115003" onChange={event => setDesignation(event.target.value)} /></Field>
      {permissions.has("documents.analyze") ? <Button type="submit" disabled={busy || active || designation.trim().length < 3}>Построить дерево</Button> : null}
      <Button type="button" variant="secondary" disabled={busy || designation.trim().length < 3} onClick={() => void act(() => loadHistory(0))}>Сохранённые результаты</Button>
    </form>
    {error ? <Callout tone="warning">{error}</Callout> : null}
    {history.map(item => <p key={item.id}><Button size="small" variant="ghost" onClick={() => setRun(item)}>{item.designation_original} · {new Date(item.created_at).toLocaleString("ru-RU")}</Button></p>)}
    {historyTotal ? <><Button size="small" disabled={busy || !historyOffset} onClick={() => void act(() => loadHistory(Math.max(0, historyOffset - 10)))}>Назад</Button>
      <Button size="small" disabled={busy || historyOffset + 10 >= historyTotal} onClick={() => void act(() => loadHistory(historyOffset + 10))}>Далее</Button></> : null}
    {active ? <Spinner label="Строим дерево документации" /> : null}
    {run?.status === "failed" ? <Callout tone="danger">Не удалось построить дерево. Проверьте состояние задания и worker.</Callout> : null}
    {graph ? <><p>Объектов: {run?.summary.objects ?? 0}; найденных файлов: {run?.summary.found_files ?? 0}; ожидаемых файлов не найдено: {run?.summary.missing_expected_files ?? 0}; неоднозначных: {run?.summary.ambiguous ?? 0}; не обработано: {run?.summary.unprocessed ?? 0}.</p>
      <Callout tone="warning">Это предварительная комплектность, не заключение о соответствии: правила обязательного состава не определены.
        {run?.warnings.length ? <ul>{run.warnings.map(code => <li key={code}>{warningNames[code] ?? "Ограничение построения"}</li>)}</ul> : null}</Callout>
      <ul>{(children.get(null) ?? []).map(renderOccurrence)}</ul>
      {selected ? <section><h4>{selected.node.designation_original ?? selected.node.name}</h4>
        <p>{statuses[selected.node.search_status]} · Актуальность редакции неизвестна.</p>
        {selected.node.warnings.map(code => <p key={code}>{warningNames[code] ?? "Требуется проверка"}</p>)}
        {selected.node.candidates.map(candidate => <p key={candidate.file_id}>{candidate.filenames.join(", ")} · {candidate.sources.join(", ")} · {candidate.accepted ? "Кандидат по обозначению и типу" : candidate.rejection_reasons.map(code => reasons[code] ?? "Недостаточно оснований").join("; ")}
          {candidate.bases.includes("text") ? " · есть упоминание в тексте" : ""}</p>)}
        {selected.edge ? <><p>{relations[selected.edge.relation_type]} · Страница {selected.edge.evidence.page ?? "не установлена"} · {selected.edge.confirmation_status === "requires_review" ? "Требуется проверка" : "Автоматическое извлечение"}</p>
          <pre style={{ whiteSpace: "pre-wrap" }}>{selected.edge.evidence.source_fragment}</pre>
          {pdf ? <iframe title="PDF — источник связи" src={`${pdf}#page=${selected.edge.evidence.page ?? 1}`} style={{ width: "100%", height: 480 }} /> : null}</> : null}
      </section> : null}
      {permissions.has("documents.analyze") && run ? <Button disabled={busy || active} variant="secondary" onClick={() => void act(async () => { setRun(await api.rebuildDocumentTree(run.id)); })}>Перестроить по текущему каталогу</Button> : null}
    </> : null}
  </details>;
}
